// Issue #422, Session 78s.1. The part of an import that is the same whatever
// is being imported.
//
// In plain terms: reading a spreadsheet, working out which column is which,
// and reading a cell as text, a number or a yes/no. None of that knows or
// cares whether the file holds vendors, vendor prices or catalog items.
//
// Approved gate: docs/gates/Stede_MasterDataImport_Gate_422.html, decision M22
// — one flow, one descriptor per import type, and the component never branches
// on type. This module is the half of M22 that is not the component: everything
// three import types share, extracted so that a fourth type is a new descriptor
// and no edit to anything in here.
//
// WHY THIS IS AN EXTRACTION RATHER THAN A NEW MODULE, and it matters for trust:
// every function below was moved out of vendorCatalogImport.js unchanged except
// where a hard-coded vendor assumption had to become a parameter. That module
// still exports all of them, so its forty-odd existing tests exercise this code
// through their original entry points and would fail if the move altered
// behavior. Locked rule 14 — the duplication being removed is the mutation and
// side-effect sequence, not merely the UI shape.
//
// #466 lives here, in parseCsv and findEncodingDamage. A second parser would
// meet the lone-carriage-return fault and the encoding-window fault again,
// separately, and that is the argument for this file existing at all.

// ── D12. The same ceiling the exports use, not a second number to explain.
export const IMPORT_HARD_CAP = 25000;

// ── Reading an .xlsx workbook ────────────────────────────────────────────────
//
// Gate decision M14, built at Session 78u: Stede reads the workbook it handed
// you, so there is no Save-As-CSV step. Oracle FBDI needs its macro button only
// because its uploader takes CSV alone; removing that step also removes the
// whole class of fault #466 was about, because nobody has to choose an encoding.
//
// SheetJS rather than ExcelJS, deliberately. ExcelJS is dynamically imported on
// the DOWNLOAD path and is ~925 KiB; SheetJS is already a dependency, already
// dynamically imported by two other screens, and reading is all that is needed
// here. Loading the bigger library to read a file nobody has written to yet
// would put its weight on the upload path for no gain.
//
// EVERY SHEET IS RETURNED, and choosing between them is the caller's job. The
// workbook carries three - How to use this sheet, the data sheet, and Lists -
// and the right one is "whichever the descriptor can find its headings in".
// That is the same content-matching rule findHeaderRow already applies to rows,
// applied to sheets, and it means renaming a tab breaks nothing.
export async function readWorkbookSheets(arrayBuffer) {
  const XLSX = await import('xlsx');
  const wb = XLSX.read(arrayBuffer, { type: 'array' });
  return (wb.SheetNames || []).map(name => ({
    name,
    // `header: 1` gives the same array-of-arrays shape parseCsv returns, and
    // `raw: false` gives the FORMATTED text - so a date or a price reads the
    // way the person typing it saw it, exactly as it would through a CSV.
    rows: XLSX.utils.sheet_to_json(wb.Sheets[name], {
      header: 1, blankrows: false, raw: false, defval: '',
    }),
  }));
}

export function isWorkbookFile(file) {
  return /\.xlsx$/i.test((file && file.name) || '');
}

// ── The CSV parser ───────────────────────────────────────────────────────────
//
// Hand-written rather than pulled in, because the only file format this has to
// read is the one the export writes, plus whatever Excel produces from it.
// RFC-4180: fields may be quoted, a quote inside a quoted field is doubled, and
// a quoted field may contain commas and newlines.
export function parseCsv(text) {
  // THE BOM. `toCsv` prepends one so Excel does not mangle accented names, and
  // it lands on the first character of the file — so the first header of a
  // freshly exported file reads as "﻿Stede ID" and matches nothing. This
  // single line is the whole round trip working or not working.
  const s = String(text || '').replace(/^﻿/, '');

  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;
  let i = 0;

  while (i < s.length) {
    const c = s[i];

    if (inQuotes) {
      if (c === '"') {
        if (s[i + 1] === '"') { field += '"'; i += 2; continue; }
        inQuotes = false; i += 1; continue;
      }
      field += c; i += 1; continue;
    }

    if (c === '"') { inQuotes = true; i += 1; continue; }
    if (c === ',') { row.push(field); field = ''; i += 1; continue; }
    // #466. A LONE carriage return ends a row too. Excel's "CSV (Macintosh)"
    // format writes CR-only line endings - it means Mac OS 9, not a modern
    // Mac, and the name is the trap: two of the four people filling these in
    // work on iMacs and would reasonably choose it. This used to skip the CR
    // and wait for a line feed that never came, so the whole file parsed as
    // ONE row. It did not error; it showed a single nonsense record, which is
    // the worst shape of failure. CR+LF is still one break, not two.
    if (c === '\r') {
      row.push(field); rows.push(row); row = []; field = '';
      i += (s[i + 1] === '\n') ? 2 : 1;
      continue;
    }
    if (c === '\n') { row.push(field); rows.push(row); row = []; field = ''; i += 1; continue; }
    field += c; i += 1;
  }
  row.push(field);
  rows.push(row);

  // Drop trailing rows that are entirely empty — a file ending in a newline
  // produces one, and so does Excel with enthusiasm.
  while (rows.length && rows[rows.length - 1].every(c => String(c).trim() === '')) rows.pop();
  return rows;
}

// ── Mangled characters, and why U+FFFD is a reliable signal ──────────────
//
// #466. Excel offers four CSV formats. Two of them - "CSV (Comma delimited)"
// and "CSV (MS-DOS)" - write a legacy single-byte encoding, while the browser's
// readAsText() decodes as UTF-8. Every byte that is not valid UTF-8 becomes the
// replacement character, so "Peña" arrives as "Pe" + U+FFFD + "a" and would be
// written into the catalog that way. Excel's autocorrect turning a straight
// apostrophe curly hits the same fault.
//
// U+FFFD appears nowhere in legitimate data - it exists only to mark a decode
// that already failed - so finding one is proof rather than a guess. That is
// what makes this worth checking and a character-frequency heuristic not.
//
// It WARNS rather than stops. The rest of every row is intact, one bad
// character in a name is fixable in Stede afterwards, and refusing the file
// would cost more than it saves. The sample is returned so the person can see
// what to look for - a count on its own tells them nothing.
export function findEncodingDamage(text) {
  const s = String(text || '');
  let count = 0;
  let first = -1;
  for (let i = 0; i < s.length; i++) {
    if (s[i] === '�') { count += 1; if (first === -1) first = i; }
  }
  if (count === 0) return null;

  // Show the FIELD the damage is in, not a window of characters either side of
  // it. A fixed window crosses the comma and the line break and hands back
  // something like  e","City" "Pe<bad>a Sign Supply"  -- which is true and
  // unreadable. Walking out to the nearest delimiter gives  Pe<bad>a Sign Supply,
  // which is what somebody actually has to go and look for in the spreadsheet.
  const DELIM = ',' + String.fromCharCode(13) + String.fromCharCode(10);
  let from = first;
  while (from > 0 && DELIM.indexOf(s[from - 1]) === -1) from -= 1;
  let to = first;
  while (to < s.length && DELIM.indexOf(s[to]) === -1) to += 1;
  const sample = s.slice(from, to).trim().replace(/^"|"$/g, '');

  return { count, sample };
}

export function normHeader(h) {
  return String(h || '')
    .replace(/^﻿/, '')   // the export's own BOM — see parseCsv
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9 ]+/g, '')
    .replace(/\s+/g, ' ')
    // TRIMMED AGAIN, AT THE END, and this line is load-bearing rather than
    // tidy. Stripping punctuation turns "Part #" into "part " — a trailing
    // space no alias carries — so the column matched NOTHING and was offered
    // for manual mapping as though Stede had never heard of it. Silent, and it
    // hits most supplier price lists, whose headings end in "#", "." or ":".
    // Found at 78s.1 by a test written against a real-looking vendor file.
    .trim();
}

// ── Matching the file's columns to ours ──────────────────────────────────────
//
// The alias table is the only thing that differs between import types, so it is
// the parameter and everything else below is shared. `signature` is an optional
// list of headings that, if present, mean the file almost certainly came from a
// particular system — used to say so on screen and to change no behavior, so a
// false positive costs a sentence rather than data.
export function makeHeaderMatcher(aliases, { signature = [] } = {}) {
  // Returns the column key a header feeds, plus WHERE in that key's alias list
  // it matched. The rank is what settles a file offering two candidates for the
  // same field — see mapHeaders.
  function resolveHeaderRanked(cell) {
    const n = normHeader(cell);
    if (!n) return null;
    for (const [key, list] of Object.entries(aliases)) {
      const rank = list.indexOf(n);
      if (rank !== -1) return { key, rank };
    }
    return null;
  }

  function resolveHeader(cell) {
    return resolveHeaderRanked(cell)?.key ?? null;
  }

  // D1's other half. The export writes a provenance block above the data, so
  // the header row is not row 0 in a round-tripped file. It is found by content
  // rather than by counting lines.
  //
  // Scored rather than first-past-the-post. An earlier version returned the
  // first row with at least TWO recognized cells, which rejected a perfectly
  // legitimate one-column file of vendor names outright — the person got "no
  // column headings were found" for a file whose heading was right there.
  // Taking the best-scoring row is correct for both: a provenance line scores
  // zero, and the real header row always scores at least one.
  function findHeaderRow(rows) {
    let best = -1;
    let bestScore = 0;
    for (let r = 0; r < Math.min(rows.length, 40); r += 1) {
      const score = rows[r].filter(cell => resolveHeader(cell) != null).length;
      if (score > bestScore) { bestScore = score; best = r; }
    }
    return best;
  }

  // Map the file's columns onto ours. Returns which of our columns each file
  // column feeds, which file columns were not understood, and which of our keys
  // were found at all.
  //
  // D6: when `unmatched` is empty and the descriptor's required keys were all
  // found, the mapping step is skipped entirely.
  function mapHeaders(headerCells) {
    const cells = headerCells || [];
    const unmatched = [];      // understood by nobody — these are what D6 asks about
    const superseded = [];     // understood, but another column won the same field
    const winners = new Map(); // our key -> { index, rank }

    cells.forEach((cell, index) => {
      const hit = resolveHeaderRanked(cell);
      const label = String(cell || '').trim();
      if (!hit) {
        if (label) unmatched.push({ index, header: label });
        return;
      }
      const current = winners.get(hit.key);
      // ALIAS PRIORITY, NOT COLUMN ORDER, and the difference is D14's whole
      // point. A QuickBooks export carries both `Display Name` and `Company`,
      // with Company FIRST — and DisplayName is the supplier's identity in
      // QuickBooks and the string its API returns, so it is the one that has to
      // win if the linking step afterwards is going to match. Taking the
      // left-most column would have quietly picked the wrong one.
      if (!current || hit.rank < current.rank) {
        if (current) superseded.push({ index: current.index, header: String(cells[current.index] || '').trim(), duplicateOf: hit.key });
        winners.set(hit.key, { index, rank: hit.rank });
      } else {
        superseded.push({ index, header: label, duplicateOf: hit.key });
      }
    });

    const mapping = {};
    for (const [key, w] of winners) mapping[w.index] = key;

    const norm = cells.map(normHeader);

    return {
      mapping,
      unmatched,
      // A superseded column is ignored, not asked about. Surfacing it in the
      // mapping step would force a manual step on every QuickBooks file, which
      // is exactly the tax D6 exists to remove.
      superseded,
      matchedCount: Object.keys(mapping).length,
      // Every non-empty column in the file, so the mapping step can say "13 of
      // 15" and have the 15 be the number the person counts in their
      // spreadsheet.
      totalColumns: cells.filter(c => String(c || '').trim() !== '').length,
      // Which of OUR keys the file supplied. Generic where the vendor importer
      // used to carry hasName/hasId — a descriptor states its own required set
      // and this answers it without this module knowing what a vendor is.
      found: new Set(winners.keys()),
      looksLikeSignature: signature.length > 0 && signature.some(sig => norm.includes(sig)),
    };
  }

  return { resolveHeader, findHeaderRow, mapHeaders };
}

// ── Cell readers ─────────────────────────────────────────────────────────────

// D3, and it is the single most important function in this file. A blank cell
// means "leave this alone" — it NEVER clears a stored value. Returned as
// undefined so the planner can tell "not supplied" from "supplied as empty",
// which a null could not express.
export function textCell(raw) {
  const v = String(raw ?? '').trim();
  return v === '' ? undefined : v;
}

const TRUE_WORDS = ['yes', 'y', 'true', '1', 'active'];
const FALSE_WORDS = ['no', 'n', 'false', '0', 'inactive'];

export function boolCell(raw) {
  const v = String(raw ?? '').trim().toLowerCase();
  if (v === '') return undefined;
  if (TRUE_WORDS.includes(v)) return true;
  if (FALSE_WORDS.includes(v)) return false;
  return { error: `“${String(raw).trim()}” is not a yes or no.` };
}

// A price out of a spreadsheet arrives wearing whatever the vendor's own sheet
// wore: a currency symbol, thousands separators, sometimes parentheses where a
// credit was meant. Stripped rather than refused, because "$1,247.50" is not a
// person making a mistake — it is a person pasting their supplier's price list,
// which is the entire point of this import.
//
// A NEGATIVE price IS refused. #299 established that Stede stores money as a
// positive amount and expresses direction with the record type; a negative here
// would be someone's credit note in the wrong file.
export function moneyCell(raw) {
  const v = String(raw ?? '').trim();
  if (v === '') return undefined;
  const cleaned = v.replace(/[$\s,]/g, '');
  if (!/^-?\d*\.?\d+$/.test(cleaned)) {
    return { error: `“${v}” is not a price. Enter it as a number, like 247.50.` };
  }
  const n = Number(cleaned);
  if (!Number.isFinite(n)) return { error: `“${v}” is not a price.` };
  if (n < 0) return { error: `A price cannot be negative. “${v}” looks like a credit rather than a price.` };
  return n;
}

// Whole numbers only, and a zero is refused rather than accepted. Pack quantity
// is the divisor Postgres uses to compute unit_cost — a zero would make the
// generated column null and the item's cost unknowable, silently.
export function intCell(raw, { min = 1 } = {}) {
  const v = String(raw ?? '').trim();
  if (v === '') return undefined;
  const cleaned = v.replace(/[\s,]/g, '');
  if (!/^\d+$/.test(cleaned)) {
    return { error: `“${v}” is not a whole number.` };
  }
  const n = Number(cleaned);
  if (n < min) return { error: `“${v}” must be at least ${min}.` };
  return n;
}

// ── The shared shape of a preview ────────────────────────────────────────────
//
// Every import type produces a list of entries carrying the same five things —
// mode, the line the person can scroll to, what will change, what is wrong and
// what is merely worth knowing — so the preview screen, the tallies and the
// rejected-rows file are written once.
export function planSummary(plan) {
  return {
    create: plan.filter(e => e.mode === 'create').length,
    update: plan.filter(e => e.mode === 'update').length,
    unchanged: plan.filter(e => e.mode === 'unchanged').length,
    error: plan.filter(e => e.mode === 'error').length,
    warnings: plan.filter(e => e.warnings.length).length,
    // D4 and callout D — the number ON the button, so the label and the result
    // cannot disagree.
    willWrite: plan.filter(e => e.mode === 'create' || e.mode === 'update').length,
  };
}

// Callout I. The rows that did not go in, handed back as a file with the same
// columns, so the fix is made in a two-row spreadsheet rather than by hunting
// for row 17 in a file of four hundred.
export function rejectedRowsCsv(rows, headerRowIndex, plan) {
  const bad = plan.filter(e => e.mode === 'error');
  if (!bad.length) return null;
  const esc = c => `"${String(c ?? '').replace(/"/g, '""')}"`;
  const header = rows[headerRowIndex];
  const lines = [
    [...header, 'Why it was skipped'].map(esc).join(','),
    ...bad.map(e => [...(rows[e.lineNo - 1] || []), e.errors.join(' ')].map(esc).join(',')),
  ];
  return '﻿' + lines.join('\r\n');
}
